import { Button, Flex, Grid, message, Switch, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import ReactFlow, {
    Background,
    BackgroundVariant,
    ReactFlowProvider,
    type Edge,
    type Node,
    type NodeChange,
    type ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import Drawer from "../../components/custom/drawer/Drawer";
import HButton from "../../components/custom/input/button/Button";
import { AppContext } from "../../context/AppContext";
import HInput from "../../components/custom/input/input/Input";
import useHttp from "../../hooks/useHttp";
import { successNotification } from "../utils/CommonNotifications";
import { CommonLoadingV2, CommonModal, DeleteComponent } from "../utils/CommonVessels";
import { ActionConfigPanel } from "./components/ActionConfigPanel";
import { ConditionConfigPanel } from "./components/ConditionConfigPanel";
import { WorkflowCustomControls } from "./components/WorkflowCustomControls";
import {
    ADD_STEP_GAP,
    ADD_STEP_SIZE,
    DEFAULT_NODE_HEIGHT,
    NODE_WIDTH,
    NODE_Y_GAP,
} from "./constants/workflowConstants";
import { ConnectorEdge } from "./edges/ConnectorEdge";
import type { WorkflowEnumItem, WorkflowEnumOption } from "./hooks/useWorkflowEnums";
import { useWorkflowEnums } from "./hooks/useWorkflowEnums";
import { ActionNode } from "./nodes/ActionNode";
import { AddStepNode } from "./nodes/AddStepNode";
import { ConditionNode } from "./nodes/ConditionNode";
import { EndNode } from "./nodes/EndNode";
import { Trigger, TriggerNode, type TriggerType } from "./nodes/TriggerNode";
import type { ServerWorkflow, StepDefinition } from "./parser";
import { useWorkflowStore } from "./store";
import { selectHasChanged, selectSelectedStep } from "./store/selectors";
import "./triggerRules.css";
import type { WorkflowRecord } from "./TriggerRulesList";
import { getNotifyUserStaticMessageFromActionTypes } from "./utils/notifyUserStaticMessage";

const { Text } = Typography;

const REACT_FLOW_PRO_OPTIONS = { hideAttribution: true } as const;
const REACT_FLOW_DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 };

interface WorkflowConfigPanelBodyProps {
    selectedStep: StepDefinition | null;
    filteredConditionTypes: WorkflowEnumItem[];
    savedNumbers: Array<{ phoneNumber?: string; friendlyName?: string; teamName?: string; integId?: string }>;
    scheduleOptions: WorkflowEnumOption[];
    filteredActionTypes: WorkflowEnumItem[];
    storeTriggerType: TriggerType;
    onQuickAddStep: (type: "condition" | "action") => void;
    isNarrowLayout: boolean;
}

const WorkflowConfigPanelBody: React.FC<WorkflowConfigPanelBodyProps> = ({
    selectedStep,
    filteredConditionTypes,
    savedNumbers,
    scheduleOptions,
    filteredActionTypes,
    storeTriggerType,
    onQuickAddStep,
    isNarrowLayout,
}) => {
    if (selectedStep?.type === "condition") {
        return (
            <ConditionConfigPanel
                conditionTypes={filteredConditionTypes}
                savedNumbers={savedNumbers}
                scheduleOptions={scheduleOptions}
                isNarrowLayout={isNarrowLayout}
            />
        );
    }
    if (selectedStep?.type === "action") {
        return <ActionConfigPanel actionTypes={filteredActionTypes} isNarrowLayout={isNarrowLayout} />;
    }
    return (
        <div className="d-flex flex-column h-100">
            <div className="trigger-rule-selector-panel-header">
                <Text className="hs-fw-600 hs-fs-14 hs-color-BlackRussian">Trigger</Text>
            </div>
            <div className="flex-fill trigger-rule-config-panel-body-empty d-flex align-items-center justify-content-center overflow-hidden py-4 px-3">
                <div className="trigger-rule-empty-state d-flex flex-column align-items-center gap-2">
                    <div className="automationCardsSprite trigger-rule-empty-state-icon" />
                    <Text className="hs-fs-18 hs-color-BlackRussian hs-fw-600">Trigger is ready</Text>
                    {storeTriggerType === Trigger.Message.Outbound ? (
                        <Text className="hs-fs-12 hs-color-mediumDark">This predefined flow cannot be modified.</Text>
                    ) : (
                        <>
                            <Text className="hs-fs-12 hs-color-mediumDark">
                                Define your rule with a condition or action.
                            </Text>
                            <button
                                type="button"
                                className="text-decoration-underline hs-fs-13 hs-color-violet cursor-pointer border-0 bg-transparent"
                                onClick={() => onQuickAddStep("condition")}
                            >
                                Add Condition
                            </button>
                            <button
                                type="button"
                                className="text-decoration-underline hs-fs-13 hs-color-violet cursor-pointer border-0 bg-transparent"
                                onClick={() => onQuickAddStep("action")}
                            >
                                Add Action
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const TRIGGER_TYPE_TO_CODE: Record<TriggerType, string> = {
    [Trigger.Message.Inbound]: Trigger.Message.Inbound,
    [Trigger.Message.Outbound]: Trigger.Message.Outbound,
};

function filterBySupportedTriggers<T extends WorkflowEnumItem>(items: T[], triggerLabel: string): T[] {
    return items.filter((item) => {
        const supported = item.meta?.supportedTriggers;
        if (!supported || supported.length === 0) return true;
        return supported.includes(triggerLabel);
    });
}

function isParentStepConfigured(step: StepDefinition): boolean {
    if (step.type === "trigger") return true;

    if (step.type === "condition") {
        const { data } = step;
        return !data.isPlaceholder && Boolean(data.conditionCode);
    }

    const { data } = step;
    return !data.isPlaceholder && Boolean(data.actionCode || data.actionType);
}

function buildFlowFromSteps(
    steps: StepDefinition[],
    selectedNodeId: string | null,
    measuredHeights: Record<string, number>,
    triggerType: TriggerType
): { nodes: Node[]; edges: Edge[] } {
    const isFlowEditable = triggerType !== Trigger.Message.Outbound;
    const hasAnyPlaceholder = steps.some((s) => s.type !== "trigger" && !isParentStepConfigured(s));
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const nodeX = 0;
    const addStepX = (NODE_WIDTH - ADD_STEP_SIZE) / 2;

    let y = 0;

    for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const nodeHeight = measuredHeights[step.id] ?? DEFAULT_NODE_HEIGHT;

        nodes.push({
            id: step.id,
            type: step.type,
            position: { x: nodeX, y },
            data: step.data,
            draggable: false,
            selected: selectedNodeId === step.id,
        });

        const addStepId = `addstep-after-${step.id}`;
        const addStepY = y + nodeHeight + (NODE_Y_GAP - ADD_STEP_SIZE) / 2;

        nodes.push({
            id: addStepId,
            type: "addStep",
            position: { x: addStepX, y: addStepY },
            data: {
                insertIndex: i + 1,
                parentStepId: step.id,
                isEnabled: !hasAnyPlaceholder && isParentStepConfigured(step) && isFlowEditable,
            },
            draggable: false,
        });

        edges.push({
            id: `e-${step.id}-${addStepId}`,
            source: step.id,
            target: addStepId,
        });

        if (i + 1 < steps.length) {
            edges.push({
                id: `e-${addStepId}-${steps[i + 1].id}`,
                source: addStepId,
                target: steps[i + 1].id,
            });
        }

        y += nodeHeight + NODE_Y_GAP;
    }

    if (steps.length > 0) {
        const lastStep = steps[steps.length - 1];
        const lastStepHeight = measuredHeights[lastStep.id] ?? DEFAULT_NODE_HEIGHT;
        const lastStepY = y - (lastStepHeight + ADD_STEP_GAP);
        const lastAddStepId = `addstep-after-${lastStep.id}`;
        const lastAddStepY = lastStepY + lastStepHeight + (ADD_STEP_GAP - ADD_STEP_SIZE) / 2;
        const endNodeId = "workflow-end-node";

        nodes.push({
            id: endNodeId,
            type: "end",
            position: { x: addStepX - 6, y: lastAddStepY + ADD_STEP_SIZE + 10 },
            data: {},
            draggable: false,
            selectable: false,
        });
        edges.push({
            id: `e-${lastAddStepId}-${endNodeId}`,
            source: lastAddStepId,
            target: endNodeId,
        });
    }

    return { nodes, edges };
}

export interface TriggerRulesEditorProps {
    workflow: WorkflowRecord | null;
    serverWorkflow?: ServerWorkflow | null;
    onBack: () => void;
    onWorkflowSaved?: (workflowId: string) => void;
    triggerType?: TriggerType;
}

const TriggerRulesEditorContent: React.FC<TriggerRulesEditorProps> = ({
    workflow,
    serverWorkflow,
    onBack,
    onWorkflowSaved,
    triggerType = Trigger.Message.Inbound,
}) => {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const { conditionTypes, actionTypes, scheduleOptions } = useWorkflowEnums();

    const storeTriggerType = useWorkflowStore((s) => s.triggerType);
    const triggerCode = TRIGGER_TYPE_TO_CODE[storeTriggerType];
    const filteredConditionTypes = useMemo(
        () => filterBySupportedTriggers(conditionTypes, triggerCode),
        [conditionTypes, triggerCode]
    );
    const filteredActionTypes = useMemo(
        () => filterBySupportedTriggers(actionTypes, triggerCode),
        [actionTypes, triggerCode]
    );

    useEffect(() => {
        useWorkflowStore.getState().setNotifyUserStaticMessage(getNotifyUserStaticMessageFromActionTypes(actionTypes));
    }, [actionTypes]);

    const nodeTypes = useMemo(
        () => ({
            trigger: TriggerNode,
            condition: ConditionNode,
            action: ActionNode,
            addStep: AddStepNode,
            end: EndNode,
        }),
        []
    );
    const edgeTypes = useMemo(() => ({ default: ConnectorEdge }), []);

    const steps = useWorkflowStore((s) => s.steps);
    const selectedNodeId = useWorkflowStore((s) => s.selectedNodeId);
    const workflowName = useWorkflowStore((s) => s.workflowName);
    const measuredHeights = useWorkflowStore((s) => s.measuredHeights);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const setWorkflowName = useWorkflowStore((s) => s.setWorkflowName);
    const updateMeasuredHeights = useWorkflowStore((s) => s.updateMeasuredHeights);
    const deleteStep = useWorkflowStore((s) => s.deleteStep);
    const addStep = useWorkflowStore((s) => s.addStep);
    const incrementCloseSignal = useWorkflowStore((s) => s.incrementCloseSignal);

    const selectedStep = useWorkflowStore(selectSelectedStep);
    const hasChanged = useWorkflowStore(selectHasChanged);
    const serverStatus = useWorkflowStore((s) => s.serverStatus);
    const workflowId = useWorkflowStore((s) => s.workflowId);
    const panelHasDraft = useWorkflowStore((s) => s.panelHasDraft);
    const [saving, setSaving] = useState(false);
    const [toggleStatusLoading, setToggleStatusLoading] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [showDeleteStepConfirm, setShowDeleteStepConfirm] = useState(false);
    const [nodeIdToDelete, setNodeIdToDelete] = useState<string | null>(null);
    const [pendingSelectNodeId, setPendingSelectNodeId] = useState<string | "dismiss" | null>(null);

    const { md: greaterThanMediumScreen, lg: greaterThanOrEqualLargeScreen } = Grid.useBreakpoint();
    const isNarrowLayout = !greaterThanOrEqualLargeScreen;
    const useFixedSidePanel = greaterThanOrEqualLargeScreen;

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        const currentlyHasChanged = selectHasChanged(useWorkflowStore.getState());
        return (
            currentlyHasChanged &&
            (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
        );
    });

    useEffect(() => {
        if (serverWorkflow?.graph) {
            useWorkflowStore.getState().loadFromServer(serverWorkflow);
        } else if (workflow == null) {
            useWorkflowStore.getState().initNewWorkflow(triggerType);
        }
    }, [serverWorkflow, triggerType, workflow]);

    useEffect(() => {
        if (workflow?.workflowName) {
            setWorkflowName(workflow.workflowName);
        }
    }, [workflow?.workflowName, setWorkflowName]);

    useEffect(() => {
        return () => {
            useWorkflowStore.getState().clearStore();
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasChanged) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasChanged]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const {
                selectedNodeId: currentSelectedId,
                triggerType: currentTriggerType,
                steps: currentSteps,
            } = useWorkflowStore.getState();
            if (!currentSelectedId) return;
            if (currentTriggerType === Trigger.Message.Outbound) return;

            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;

            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                const step = currentSteps.find((s) => s.id === currentSelectedId);
                const isSaved = step && step.type !== "trigger" && !step.data.isPlaceholder;
                if (isSaved) {
                    setNodeIdToDelete(currentSelectedId);
                    setShowDeleteStepConfirm(true);
                } else {
                    deleteStep(currentSelectedId);
                }
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [deleteStep]);

    const { nodes, edges } = useMemo(
        () => buildFlowFromSteps(steps, selectedNodeId, measuredHeights, storeTriggerType),
        [steps, selectedNodeId, measuredHeights, storeTriggerType]
    );

    const handleNodesChange = useCallback(
        (changes: NodeChange[]) => {
            const heightChanges: Array<{ id: string; height: number }> = [];
            for (const change of changes) {
                if (change.type === "dimensions" && change.dimensions?.height) {
                    heightChanges.push({ id: change.id, height: change.dimensions.height });
                }
            }
            if (heightChanges.length > 0) {
                requestAnimationFrame(() => updateMeasuredHeights(heightChanges));
            }
        },
        [updateMeasuredHeights]
    );

    const onNodeClick = useCallback(
        (_: React.MouseEvent, node: Node) => {
            if (node.type === "condition" || node.type === "action") {
                if (panelHasDraft && node.id !== selectedNodeId) {
                    setPendingSelectNodeId(node.id);
                    return;
                }
                selectNode(node.id);
            }
        },
        [selectNode, panelHasDraft, selectedNodeId]
    );

    const executeDismissConfigPanel = useCallback(() => {
        incrementCloseSignal();

        if (selectedNodeId) {
            const step = steps.find((s) => s.id === selectedNodeId);
            if (step && step.type !== "trigger") {
                const isPlaceholder =
                    step.data.isPlaceholder ??
                    (step.type === "condition"
                        ? !step.data.conditionCode
                        : !(step.data.actionCode || step.data.actionType));
                if (isPlaceholder) {
                    deleteStep(selectedNodeId);
                }
            }
        }
        selectNode(null);
    }, [selectNode, incrementCloseSignal, deleteStep, selectedNodeId, steps]);

    const handleDismissConfigPanel = useCallback(() => {
        if (panelHasDraft) {
            setPendingSelectNodeId("dismiss");
            return;
        }
        executeDismissConfigPanel();
    }, [panelHasDraft, executeDismissConfigPanel]);

    const onPaneClick = useCallback(
        (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.(".add-step-popover-overlay") || target?.closest?.(".add-step-popover")) {
                return;
            }
            handleDismissConfigPanel();
        },
        [handleDismissConfigPanel]
    );

    const onMoveStart = useCallback(() => {
        incrementCloseSignal();
    }, [incrementCloseSignal]);

    const handleCancel = useCallback(() => {
        if (hasChanged) {
            setShowUnsavedModal(true);
        } else {
            onBack();
        }
    }, [onBack, hasChanged]);

    const handleConfirmDiscardPanelChanges = useCallback(() => {
        const pending = pendingSelectNodeId;
        setPendingSelectNodeId(null);
        if (pending === "dismiss") {
            executeDismissConfigPanel();
        } else if (pending) {
            selectNode(pending);
        }
    }, [pendingSelectNodeId, executeDismissConfigPanel, selectNode]);

    const handleCloseUnsavedModal = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        setShowUnsavedModal(false);
    }, [blocker]);

    const discardChanges = useWorkflowStore((s) => s.discardChanges);

    const handleDiscardUnsavedChanges = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.proceed();
        } else {
            discardChanges();
            setShowUnsavedModal(false);
            onBack();
        }
    }, [blocker, onBack, discardChanges]);

    const handleDeleteStepConfirm = useCallback(async () => {
        if (nodeIdToDelete) {
            deleteStep(nodeIdToDelete);
            setNodeIdToDelete(null);
        }
    }, [nodeIdToDelete, deleteStep]);

    const handleDeleteStepModalOpen = useCallback((open: boolean) => {
        setShowDeleteStepConfirm(open);
        if (!open) {
            setNodeIdToDelete(null);
        }
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const wasNewWorkflow = !workflow && !workflowId;
            const saveResult = await useWorkflowStore.getState().saveWorkflow({ fetchData, appProperties });

            if (!saveResult.success) {
                if (saveResult.validationErrors.length > 0) {
                    const firstValidationError = saveResult.validationErrors[0];
                    message.warning(firstValidationError.message);
                    if (firstValidationError.stepId) {
                        selectNode(firstValidationError.stepId);
                    }
                } else {
                    message.error(saveResult.apiErrorMessage ?? "Failed to save rule. Please try again.");
                }
                return;
            }

            successNotification("Rule saved successfully.");
            if (wasNewWorkflow) {
                const newWorkflowId = useWorkflowStore.getState().workflowId;
                if (newWorkflowId) onWorkflowSaved?.(newWorkflowId);
            }
        } catch {
            message.error("Failed to save rule.");
        } finally {
            setSaving(false);
        }
    }, [fetchData, appProperties, selectNode, workflow, workflowId, onWorkflowSaved]);

    const isWorkflowEnabled = (serverStatus ?? "").toLowerCase() === "active";
    const isDefaultFlowWorkflow = Boolean(workflow?.defaultFlow ?? serverWorkflow?.defaultFlow);
    const isPredefinedWorkflow = storeTriggerType === Trigger.Message.Outbound || isDefaultFlowWorkflow;

    const handleToggleWorkflowStatus = useCallback(
        async (enabled: boolean) => {
            if (!workflowId || !appProperties || isPredefinedWorkflow) return;

            setToggleStatusLoading(true);
            try {
                const result = await useWorkflowStore
                    .getState()
                    .setWorkflowEnabled({ fetchData, appProperties }, enabled);
                if (result.success) {
                    message.success(enabled ? "Rule enabled successfully." : "Rule disabled successfully.");
                } else {
                    message.error(
                        result.errorMessage ?? (enabled ? "Failed to enable rule." : "Failed to disable rule.")
                    );
                }
            } catch {
                message.error(enabled ? "Failed to enable rule." : "Failed to disable rule.");
            } finally {
                setToggleStatusLoading(false);
            }
        },
        [workflowId, appProperties, fetchData, isPredefinedWorkflow]
    );

    const handleQuickAddStep = useCallback(
        (type: "condition" | "action") => {
            if (steps.some((s) => s.type !== "trigger" && !isParentStepConfigured(s))) return;
            addStep(steps.length, type);
        },
        [addStep, steps]
    );

    const workflowStatusSwitchAriaLabel = isPredefinedWorkflow
        ? "Predefined rule — active status cannot be changed"
        : isWorkflowEnabled
          ? "Disable rule"
          : "Enable rule";

    const workflowActiveSwitchRow = (
        <Flex align="center" gap={8}>
            <Text className="hs-fs-14 hs-color-mediumDark">Active</Text>
            <Switch
                title={workflowStatusSwitchAriaLabel}
                className="trigger-rules-status-switch"
                checked={isWorkflowEnabled}
                onChange={handleToggleWorkflowStatus}
                disabled={toggleStatusLoading || !workflowId || isPredefinedWorkflow || hasChanged}
                loading={toggleStatusLoading}
                aria-label={workflowStatusSwitchAriaLabel}
            />
        </Flex>
    );

    const handleReactFlowInit = useCallback((instance: ReactFlowInstance) => {
        instance.fitView({
            padding: 0.2,
            minZoom: 0.9,
            maxZoom: 1.5,
            duration: 200,
        });
    }, []);

    const unsavedModalBody = (
        <>
            <div>
                <div className="ms-2">
                    <h5 className="hs-color-dark-blue">Unsaved changes</h5>
                    <div className="hs-color-dark-blue">Do you want to save or discard changes?</div>
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
                <Button
                    className="mt-2 row-reverse d-inline-block hs-btn-cancel-small w-100 h-45 hs-fs-14"
                    onClick={handleCloseUnsavedModal}
                >
                    Cancel
                </Button>
                <Button
                    className="hs-btn-small ms-3 mt-2 row-reverse d-inline-block w-100 h-45 confirmDeleteBtn"
                    onClick={handleDiscardUnsavedChanges}
                >
                    Discard
                </Button>
            </div>
        </>
    );

    const discardPanelChangesModalBody = (
        <>
            <div className="ms-2">
                <h5 className="hs-color-dark-blue">Discard changes?</h5>
                <div className="hs-color-dark-blue">You have unsaved changes in this step. They will be lost.</div>
            </div>
            <div className="d-flex align-items-center justify-content-end mt-3">
                <Button
                    className="mt-2 row-reverse d-inline-block hs-btn-cancel-small w-100 h-45 hs-fs-14"
                    onClick={() => setPendingSelectNodeId(null)}
                >
                    Cancel
                </Button>
                <Button
                    className="hs-btn-small ms-3 mt-2 row-reverse d-inline-block w-100 h-45 confirmDeleteBtn"
                    onClick={handleConfirmDiscardPanelChanges}
                >
                    Discard
                </Button>
            </div>
        </>
    );

    if (!appProperties) return <CommonLoadingV2 />;

    return (
        <>
            <CommonModal
                width={350}
                body={unsavedModalBody}
                open={showUnsavedModal || blocker.state === "blocked"}
                doOnClose={handleCloseUnsavedModal}
                cancelButtonProps={false}
                okButtonProps={false}
                modalCloseIcon={true}
                header={
                    <div style={{ cursor: "default" }} className="actionIconsSpriteForSideBar unsaved-modal-icon" />
                }
            />
            <CommonModal
                width={350}
                body={discardPanelChangesModalBody}
                open={pendingSelectNodeId !== null}
                doOnClose={() => setPendingSelectNodeId(null)}
                cancelButtonProps={false}
                okButtonProps={false}
                modalCloseIcon={true}
                header={
                    <div style={{ cursor: "default" }} className="actionIconsSpriteForSideBar unsaved-modal-icon" />
                }
            />
            <DeleteComponent
                modalOpen={showDeleteStepConfirm}
                setModalOpen={handleDeleteStepModalOpen}
                record={{ workflowStepId: nodeIdToDelete ?? "" }}
                confirmDeleteIcon="hs-delete-icon"
                confirmDeleteBtnName="Delete"
                confirmDeleteComponentTitle="Delete this step?"
                confirmDeleteComponent="This step will be removed from the rule. You can add it back later if needed."
                handleDelete={handleDeleteStepConfirm}
            />
            <div className="h-100 d-flex flex-row flex-fill hs-min-h-0" style={{ minWidth: 0 }}>
                <div className="d-flex flex-column flex-fill hs-min-h-0" style={{ minWidth: 0 }}>
                    <Flex
                        className="trigger-rules-editor-header py-3 px-3 hs-border-bottom-light"
                        vertical={!greaterThanMediumScreen}
                        gap={greaterThanMediumScreen ? 0 : 16}
                        align={greaterThanMediumScreen ? "center" : "stretch"}
                        justify={greaterThanMediumScreen ? "space-between" : undefined}
                        style={{ width: "100%", minWidth: 0 }}
                    >
                        <Flex
                            align="center"
                            gap={12}
                            style={{ minWidth: 0, width: greaterThanMediumScreen ? "auto" : "100%" }}
                        >
                            <HInput
                                tabIndex={1}
                                variant="borderless"
                                placeholder="Enter Friendly Name"
                                value={workflowName}
                                customClasses={"ms-3 hs-min-w-250-px"}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkflowName(e.target.value)}
                            />
                        </Flex>
                        {greaterThanMediumScreen ? (
                            <Flex gap={12} wrap="wrap" align="center" className="d-flex align-items-center">
                                {workflowActiveSwitchRow}
                                <div className="vr hs-color-mediumDark" aria-hidden />
                                <HButton type="text" onClick={handleCancel}>
                                    Cancel
                                </HButton>
                                <HButton
                                    type="primary"
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={saving || !hasChanged}
                                    buttonClassName="hs-min-w-70-px"
                                >
                                    Save
                                </HButton>
                            </Flex>
                        ) : (
                            <>
                                {workflowActiveSwitchRow}
                                <Flex gap={12} style={{ width: "100%" }}>
                                    <div className="flex-fill" style={{ minWidth: 0 }}>
                                        <HButton type="text" onClick={handleCancel} buttonClassName="w-100">
                                            Cancel
                                        </HButton>
                                    </div>
                                    <div className="flex-fill" style={{ minWidth: 0 }}>
                                        <HButton
                                            type="primary"
                                            onClick={handleSave}
                                            loading={saving}
                                            disabled={saving || !hasChanged}
                                            buttonClassName="w-100"
                                        >
                                            Save
                                        </HButton>
                                    </div>
                                </Flex>
                            </>
                        )}
                    </Flex>

                    <div className="d-flex flex-fill hs-min-h-0" role="main" aria-label="Rule builder">
                        <div className="position-relative hs-bg-light-grey-1 d-flex justify-content-center align-items-center flex-fill">
                            <ReactFlow
                                className="hs-bg-light-grey-1"
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={handleNodesChange}
                                onNodeClick={onNodeClick}
                                onPaneClick={onPaneClick}
                                onMoveStart={onMoveStart}
                                onInit={handleReactFlowInit}
                                nodeTypes={nodeTypes}
                                edgeTypes={edgeTypes}
                                onlyRenderVisibleElements
                                defaultViewport={REACT_FLOW_DEFAULT_VIEWPORT}
                                minZoom={0.3}
                                maxZoom={1.5}
                                panOnScroll
                                zoomOnScroll={false}
                                zoomOnPinch
                                nodesDraggable={false}
                                nodesConnectable={false}
                                elementsSelectable
                                proOptions={REACT_FLOW_PRO_OPTIONS}
                            >
                                <Background variant={BackgroundVariant.Dots} gap={16} size={1.5} color="#d0d0d0" />
                                <WorkflowCustomControls />
                            </ReactFlow>
                        </div>
                    </div>
                </div>
                {useFixedSidePanel ? (
                    <div className="hs-w-400-px hs-border-left-light flex-shrink-0 min-h-0 d-flex flex-column h-100">
                        <WorkflowConfigPanelBody
                            selectedStep={selectedStep}
                            filteredConditionTypes={filteredConditionTypes}
                            savedNumbers={appProperties?.savedNumbers ?? []}
                            scheduleOptions={scheduleOptions}
                            filteredActionTypes={filteredActionTypes}
                            storeTriggerType={storeTriggerType}
                            onQuickAddStep={handleQuickAddStep}
                            isNarrowLayout={isNarrowLayout}
                        />
                    </div>
                ) : (
                    <Drawer
                        destroyOnClose
                        open={selectedNodeId != null}
                        placement="right"
                        className="w-100 h-100"
                        styles={{ body: { padding: 0 } }}
                        onClose={handleDismissConfigPanel}
                    >
                        <div className="d-flex flex-column h-100 min-h-0">
                            <WorkflowConfigPanelBody
                                selectedStep={selectedStep}
                                filteredConditionTypes={filteredConditionTypes}
                                savedNumbers={appProperties?.savedNumbers ?? []}
                                scheduleOptions={scheduleOptions}
                                filteredActionTypes={filteredActionTypes}
                                storeTriggerType={storeTriggerType}
                                onQuickAddStep={handleQuickAddStep}
                                isNarrowLayout={isNarrowLayout}
                            />
                        </div>
                    </Drawer>
                )}
            </div>
        </>
    );
};

const TriggerRulesEditor: React.FC<TriggerRulesEditorProps> = (props) => (
    <ReactFlowProvider>
        <TriggerRulesEditorContent {...props} />
    </ReactFlowProvider>
);

export default TriggerRulesEditor;
